import { ChangeEvent, useEffect, useRef } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { QuestionAnswerType } from '../../../common/utils/questionAnswerTypes';
import { InterviewQuestionProps } from '../../../types/InterviewQuestionTypes';
import useSetWatchQuestion from '../hooks/useSetWatchQuestion';

export const StyledRadio = styled(Radio)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    fontSize: 32,
  },
  '&.Mui-checked': {
    width: 32,
    height: 32,
    color: theme.xPalette.completed.color,
    backgroundColor: theme.xPalette.white,
    marginRight: '8px',
  },
  '&.Mui-checked + span': {
    color: theme.xPalette.white,
  },
}));

export enum BinaryValue {
  Yes = 'yes',
  No = 'no',
}

export const radioStyle = {
  flex: 1,
  maxWidth: '50%',
  display: 'flex',
  justifyContent: 'space-between',
  borderRadius: '8px',
  paddingLeft: '16px',
  margin: 0,
  '&.MuiFormControlLabel-root:first-of-type': {
    marginRight: '4px',
  },
};

export const StyledFormLabelYes = styled(FormControlLabel)(({ theme, itemScope }) => ({
  ...radioStyle,
  border: `1px solid ${theme.xPalette.disabled}`,
  '&.MuiFormControlLabel-root': {
    backgroundColor: itemScope ? theme.xPalette.completed.color : '',
  },
}));

export const StyledFormLabelNo = styled(FormControlLabel)(({ theme, itemScope }) => ({
  ...radioStyle,
  border: `1px solid ${theme.xPalette.disabled}`,
  '&.MuiFormControlLabel-root': {
    backgroundColor: itemScope ? theme.xPalette.completed.color : '',
  },
}));

export const StyledFormControl = styled(FormControl)(({ theme, error }) => ({
  margin: '16px 0px',
  width: '40%',
  '&.MuiFormControl-root .MuiFormGroup-root .MuiFormControlLabel-root': {
    borderColor: error ? theme.palette.error.main : '',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const StyledTypography = styled(Typography)(() => ({
  fontWeight: 'bold',
  marginBottom: '8px',
}));

export default function BinaryQuestion({
  question,
  field,
  error,
  helperText,
  isStsSdsQuestion,
  setValue,
  optionChangeCallback,
  sequenceOrder,
}: InterviewQuestionProps) {
  const optionRef = useRef<HTMLDivElement>(null);
  const initialLoad = useRef<boolean>(true);
  const savedData = field?.value;

  const { handleOnBlur } = useSetWatchQuestion({
    name: field?.name!,
    type: QuestionAnswerType.Binary,
    required: !!question.isRequired,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as string;
    setValue(field?.name!, value);
    if (optionChangeCallback) {
      // set DOM attribute
      const selectedIndex = question.possibleAnswers.indexOf(event.target.value);
      optionRef.current?.setAttribute('data-selected-index', '' + (selectedIndex + 1)); // default option is in 1st position so need to adding 1
      // execute parent callback
      optionChangeCallback();
    }

    handleOnBlur();
  };
  const renderAnswers = (BinaryValue: string) => {
    const { displayAnswers, possibleAnswers } = question;

    let items: any = {};
    for (let i = 0; i < possibleAnswers.length; i++) {
      const value = possibleAnswers[i];
      const text =
        displayAnswers?.length === possibleAnswers?.length
          ? `${displayAnswers?.[i] || value}`.trim()
          : value.trim();
      items = { ...items, [value]: text };
    }

    return items[BinaryValue];
  };

  useEffect(() => {
    if (savedData && savedData !== '') {
      if (initialLoad.current) {
        initialLoad.current = false;
        setValue(field?.name!, savedData as string);
        if (optionChangeCallback) {
          // set DOM attribute
          const selectedIndex = question.possibleAnswers.indexOf(savedData);

          optionRef.current?.setAttribute('data-selected-index', '' + (selectedIndex + 1)); // default option is in 1st position so need to adding 1
          // execute parent callback
          optionChangeCallback();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field?.name, question.possibleAnswers, savedData, setValue]);

  return (
    <Box
      sx={{
        mt: 2,
      }}
      className={
        question.clientDisplayCondition
          ? 'js-has-client-display-condition'
          : 'js-question-answer-options'
      }
      ref={optionRef}
    >
      {isStsSdsQuestion && (
        <StyledTypography
          dangerouslySetInnerHTML={{
            __html: question.subTitle
              ? `${question.name}:`
              : question.title
              ? `${question.title}:`
              : '',
          }}
        ></StyledTypography>
      )}
      {question.questionPrompt && (
        <StyledTypography
          dangerouslySetInnerHTML={{
            __html: question.questionPrompt,
          }}
        ></StyledTypography>
      )}
      <Typography
        dangerouslySetInnerHTML={{
          __html: question.questionText!,
        }}
      ></Typography>
      <StyledFormControl error={error} variant="standard">
        <RadioGroup
          {...field}
          row
          onChange={handleChange}
          className={`sequence-order-${sequenceOrder}`}
        >
          <StyledFormLabelNo
            value={question.possibleAnswers[1]}
            itemScope={field?.value === question.possibleAnswers[1]}
            labelPlacement="start"
            control={<StyledRadio checkedIcon={<CheckIcon />} />}
            label={renderAnswers(question.possibleAnswers[1])}
          />
          <StyledFormLabelYes
            value={question.possibleAnswers[0]}
            itemScope={field?.value === question.possibleAnswers[0]}
            labelPlacement="start"
            control={<StyledRadio checkedIcon={<CheckIcon />} />}
            label={renderAnswers(question.possibleAnswers[0])}
          />
        </RadioGroup>
        {error && <FormHelperText>{helperText}</FormHelperText>}
      </StyledFormControl>
    </Box>
  );
}
